import * as Cesium from 'cesium';

export type ViewMode = 'overview' | 'first-person';

export interface FirstPersonController {
  readonly isActive: boolean;
  enter: () => void;
  exit: () => void;
  toggle: () => void;
  setCollisionTilesets: (tilesets: Cesium.Cesium3DTileset[]) => void;
}

const EYE_HEIGHT = 1.7;
const WALK_SPEED = 4.2;
const RUN_MULTIPLIER = 2.2;
const LOOK_SENSITIVITY = 0.0025;
const MIN_PITCH = Cesium.Math.toRadians(-72);
const MAX_PITCH = Cesium.Math.toRadians(12);
const MOVEMENT_KEYS = new Set([
  'KeyW',
  'KeyA',
  'KeyS',
  'KeyD',
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ShiftLeft',
  'ShiftRight',
]);

interface SavedCameraView {
  position: Cesium.Cartesian3;
  direction: Cesium.Cartesian3;
  up: Cesium.Cartesian3;
}

interface ControllerState {
  enableInputs: boolean;
  enableCollisionDetection: boolean;
}

function horizontalDirection(
  direction: Cesium.Cartesian3,
  position: Cesium.Cartesian3,
): Cesium.Cartesian3 {
  const up = Cesium.Ellipsoid.WGS84.geodeticSurfaceNormal(position);
  const verticalComponent = Cesium.Cartesian3.multiplyByScalar(
    up,
    Cesium.Cartesian3.dot(direction, up),
    new Cesium.Cartesian3(),
  );
  const horizontal = Cesium.Cartesian3.subtract(
    direction,
    verticalComponent,
    new Cesium.Cartesian3(),
  );
  return Cesium.Cartesian3.normalize(horizontal, horizontal);
}

export function createFirstPersonController(
  viewer: Cesium.Viewer,
  getCollisionTilesets: () => Cesium.Cesium3DTileset[],
): FirstPersonController {
  const canvas = viewer.scene.canvas;
  const cameraController = viewer.scene.screenSpaceCameraController;
  const pressedKeys = new Set<string>();
  const collisionStates = new Map<Cesium.Cesium3DTileset, boolean>();
  let collisionTilesets: Cesium.Cesium3DTileset[] = [];
  let active = false;
  let savedView: SavedCameraView | undefined;
  let savedControllerState: ControllerState | undefined;
  let frameId: number | undefined;
  let lastFrameTime = 0;
  let pointerId: number | undefined;
  let lastPointerX = 0;
  let lastPointerY = 0;

  const requestMovementLoop = (): void => {
    if (frameId !== undefined) return;
    lastFrameTime = performance.now();
    frameId = window.requestAnimationFrame(step);
  };

  const stopMovementLoop = (): void => {
    if (frameId === undefined) return;
    window.cancelAnimationFrame(frameId);
    frameId = undefined;
  };

  const terrainHeightAt = (cartographic: Cesium.Cartographic): number => {
    try {
      const sampled = viewer.scene.sampleHeight(
        Cesium.Cartographic.clone(cartographic),
        collisionTilesets,
        0.5,
      );
      if (sampled !== undefined) return sampled;
    } catch (error) {
      console.debug('[FirstPerson] terrain height sample unavailable', error);
    }
    return 0;
  };

  const isInsideBuilding = (cartographic: Cesium.Cartographic): boolean => {
    if (collisionTilesets.length === 0) return false;
    try {
      const terrainHeight = terrainHeightAt(cartographic);
      const sceneHeight = viewer.scene.sampleHeight(
        Cesium.Cartographic.clone(cartographic),
        undefined,
        0.5,
      );
      // 地形との差が大きい場合は、建物の屋根・壁の内部へ進入したと判断する。
      return sceneHeight !== undefined && sceneHeight - terrainHeight > 2.0;
    } catch (error) {
      console.debug('[FirstPerson] building collision sample unavailable', error);
      return false;
    }
  };

  const moveBy = (eastWest: number, northSouth: number): void => {
    if (!active || (eastWest === 0 && northSouth === 0)) return;

    const camera = viewer.camera;
    const currentPosition = Cesium.Cartesian3.clone(camera.positionWC);
    const forward = horizontalDirection(camera.directionWC, currentPosition);
    const right = horizontalDirection(camera.rightWC, currentPosition);
    const translation = Cesium.Cartesian3.add(
      Cesium.Cartesian3.multiplyByScalar(forward, northSouth, new Cesium.Cartesian3()),
      Cesium.Cartesian3.multiplyByScalar(right, eastWest, new Cesium.Cartesian3()),
      new Cesium.Cartesian3(),
    );
    if (Cesium.Cartesian3.magnitudeSquared(translation) < 0.0001) return;
    Cesium.Cartesian3.normalize(translation, translation);

    const currentCartographic = camera.positionCartographic;
    const currentGroundHeight = terrainHeightAt(currentCartographic);
    const currentEyeHeight = Math.max(
      EYE_HEIGHT,
      currentCartographic.height - currentGroundHeight,
    );
    const candidatePosition = Cesium.Cartesian3.add(
      currentPosition,
      Cesium.Cartesian3.multiplyByScalar(
        translation,
        Cesium.Cartesian3.magnitude(new Cesium.Cartesian3(eastWest, northSouth, 0)),
        new Cesium.Cartesian3(),
      ),
      new Cesium.Cartesian3(),
    );
    const candidateCartographic = Cesium.Cartographic.fromCartesian(candidatePosition);
    const candidateGroundHeight = terrainHeightAt(candidateCartographic);
    candidateCartographic.height = candidateGroundHeight + currentEyeHeight;

    if (isInsideBuilding(candidateCartographic)) return;

    camera.setView({
      destination: Cesium.Cartesian3.fromRadians(
        candidateCartographic.longitude,
        candidateCartographic.latitude,
        candidateCartographic.height,
      ),
      orientation: {
        heading: camera.heading,
        pitch: camera.pitch,
        roll: 0,
      },
    });
    viewer.scene.requestRender();
  };

  const step = (timestamp: number): void => {
    frameId = undefined;
    if (!active) return;

    const deltaSeconds = Math.min((timestamp - lastFrameTime) / 1000, 0.05);
    lastFrameTime = timestamp;
    const forward = pressedKeys.has('KeyW') || pressedKeys.has('ArrowUp')
      ? 1
      : pressedKeys.has('KeyS') || pressedKeys.has('ArrowDown')
        ? -1
        : 0;
    const right = pressedKeys.has('KeyD') || pressedKeys.has('ArrowRight')
      ? 1
      : pressedKeys.has('KeyA') || pressedKeys.has('ArrowLeft')
        ? -1
        : 0;

    if (forward !== 0 || right !== 0) {
      const length = Math.hypot(forward, right) || 1;
      const speed = WALK_SPEED
        * (pressedKeys.has('ShiftLeft') || pressedKeys.has('ShiftRight')
          ? RUN_MULTIPLIER
          : 1)
        * deltaSeconds;
      moveBy((right / length) * speed, (forward / length) * speed);
      requestMovementLoop();
    }
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (!active) return;
    if (!MOVEMENT_KEYS.has(event.code)) return;
    event.preventDefault();
    pressedKeys.add(event.code);
    requestMovementLoop();
  };

  const onKeyUp = (event: KeyboardEvent): void => {
    pressedKeys.delete(event.code);
  };

  const onPointerDown = (event: PointerEvent): void => {
    if (!active || (event.button !== 0 && event.pointerType !== 'touch')) return;
    pointerId = event.pointerId;
    lastPointerX = event.clientX;
    lastPointerY = event.clientY;
    canvas.setPointerCapture?.(event.pointerId);
    canvas.style.cursor = 'grabbing';
    event.preventDefault();
  };

  const onPointerMove = (event: PointerEvent): void => {
    if (!active || pointerId !== event.pointerId) return;
    const deltaX = event.clientX - lastPointerX;
    const deltaY = event.clientY - lastPointerY;
    lastPointerX = event.clientX;
    lastPointerY = event.clientY;
    const camera = viewer.camera;
    camera.setView({
      orientation: {
        heading: camera.heading - deltaX * LOOK_SENSITIVITY,
        pitch: Cesium.Math.clamp(
          camera.pitch + deltaY * LOOK_SENSITIVITY,
          MIN_PITCH,
          MAX_PITCH,
        ),
        roll: 0,
      },
    });
    viewer.scene.requestRender();
    event.preventDefault();
  };

  const releasePointer = (event: PointerEvent): void => {
    if (pointerId !== event.pointerId) return;
    pointerId = undefined;
    canvas.releasePointerCapture?.(event.pointerId);
    canvas.style.cursor = '';
  };

  const onWheel = (event: WheelEvent): void => {
    if (!active) return;
    event.preventDefault();
    const amount = Cesium.Math.clamp(-event.deltaY * 0.025, -8, 8);
    moveBy(0, amount);
  };

  const setCollisionTilesets = (tilesets: Cesium.Cesium3DTileset[]): void => {
    collisionTilesets = tilesets;
    if (!active) return;
    for (const tileset of collisionTilesets) {
      if (!collisionStates.has(tileset)) collisionStates.set(tileset, tileset.enableCollision);
      tileset.enableCollision = true;
    }
  };

  const enter = (): void => {
    if (active) return;
    viewer.camera.completeFlight();
    collisionTilesets = getCollisionTilesets();
    savedView = {
      position: Cesium.Cartesian3.clone(viewer.camera.positionWC),
      direction: Cesium.Cartesian3.clone(viewer.camera.directionWC),
      up: Cesium.Cartesian3.clone(viewer.camera.upWC),
    };
    savedControllerState = {
      enableInputs: cameraController.enableInputs,
      enableCollisionDetection: cameraController.enableCollisionDetection,
    };

    for (const tileset of collisionTilesets) {
      collisionStates.set(tileset, tileset.enableCollision);
      tileset.enableCollision = true;
    }
    cameraController.enableInputs = false;
    cameraController.enableCollisionDetection = true;

    const current = viewer.camera.positionCartographic;
    const groundHeight = terrainHeightAt(current);
    viewer.camera.setView({
      destination: Cesium.Cartesian3.fromRadians(
        current.longitude,
        current.latitude,
        groundHeight + EYE_HEIGHT,
      ),
      orientation: {
        heading: viewer.camera.heading,
        pitch: Cesium.Math.toRadians(-5),
        roll: 0,
      },
    });
    active = true;
    canvas.style.cursor = 'grab';
    viewer.scene.requestRender();
  };

  const exit = (): void => {
    if (!active) return;
    active = false;
    pressedKeys.clear();
    stopMovementLoop();
    pointerId = undefined;
    canvas.style.cursor = '';

    for (const [tileset, wasEnabled] of collisionStates) {
      tileset.enableCollision = wasEnabled;
    }
    collisionStates.clear();
    if (savedControllerState) {
      cameraController.enableInputs = savedControllerState.enableInputs;
      cameraController.enableCollisionDetection = savedControllerState.enableCollisionDetection;
    }
    if (savedView) {
      viewer.camera.setView({
        destination: savedView.position,
        orientation: {
          direction: savedView.direction,
          up: savedView.up,
        },
      });
    }
    viewer.scene.requestRender();
  };

  window.addEventListener('keydown', onKeyDown, { passive: false });
  window.addEventListener('keyup', onKeyUp);
  canvas.addEventListener('pointerdown', onPointerDown, { passive: false });
  canvas.addEventListener('pointermove', onPointerMove, { passive: false });
  canvas.addEventListener('pointerup', releasePointer);
  canvas.addEventListener('pointercancel', releasePointer);
  canvas.addEventListener('wheel', onWheel, { passive: false });

  return {
    get isActive() {
      return active;
    },
    enter,
    exit,
    toggle: () => (active ? exit() : enter()),
    setCollisionTilesets,
  };
}
